// Test comment
import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Pricing from './pages/Pricing';
import SignIn from './pages/SignIn';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Feedback from './pages/Feedback';
import architectureImage1 from './assets/architecture.png';
import architectureImage2 from './assets/architecture2.png';
import architectureImage3 from './assets/architecture3.png';
import architectureImage4 from './assets/architecture4.png';
import architectureImage5 from './assets/architecture5.png';
import architectureImage6 from './assets/architecture6.png';
import logo from './assets/logo.png';
import { subscribeToAuthChanges, signOutUser } from './services/userService';
import { useSubscription } from './hooks/useSubscription';
import ScrollToTop from './components/ScrollToTop';
import Enterprise from './pages/Enterprise';
import ContactUs from './pages/ContactUs';
import Updates from './pages/Updates';
import PastInterviews from './pages/PastInterviews';
import InterviewChat from './components/InterviewChat';
/* import HistoryPanel from './components/HistoryPanel'; */
import LandingPage from './pages/LandingPage';
import { ThemeProvider } from 'styled-components';
import { theme } from './theme';
import ShippingPolicy from './pages/ShippingPolicy';
import CancellationRefunds from './pages/CancellationRefunds';

// Add CSS for transitions
const style = document.createElement('style');
style.textContent = `
.fade-enter {
  opacity: 0;
}
.fade-enter-active {
  opacity: 1;
  transition: opacity 3000ms ease-in-out;
}
.fade-exit {
  opacity: 1;
}
.fade-exit-active {
  opacity: 0;
  transition: opacity 3000ms ease-in-out;
}

.caption-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}
.caption-fade-enter-active {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 3000ms ease-in-out, transform 3000ms ease-in-out;
}
.caption-fade-exit {
  opacity: 1;
  transform: translateY(0);
}
.caption-fade-exit-active {
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 3000ms ease-in-out, transform 3000ms ease-in-out;
}
`;
document.head.appendChild(style);

function MainContent() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const imageRef = React.useRef(null);
  const captionRef = React.useRef(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      if (!user) {
        navigate('/signin');
      } else {
        setUser(user);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const images = [
    { src: architectureImage1, caption: "The Infinity Passage" },
    { src: architectureImage2, caption: "The Library" },
    { src: architectureImage3, caption: "The Pantheon" },
    { src: architectureImage4, caption: "Residential Halls" },
    { src: architectureImage5, caption: "The Piazza" },
    { src: architectureImage6, caption: "The Orbis" }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 8000);

    return () => clearInterval(timer);
  }, [images.length]);

  // Return null while checking authentication to prevent flash of content
  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen pt-16">
      <div className="hidden lg:block w-1/3 p-8">
        <div className="relative h-[calc(100vh-8rem)] rounded-2xl overflow-hidden">
          <TransitionGroup>
            <CSSTransition
              key={currentImageIndex}
              timeout={3000}
              classNames="fade"
              nodeRef={imageRef}
            >
              <div ref={imageRef} className="absolute inset-0">
                <img
                  src={images[currentImageIndex].src}
                  alt={images[currentImageIndex].caption}
                  className="w-full h-full object-cover"
                />
              </div>
            </CSSTransition>
          </TransitionGroup>
          <TransitionGroup>
            <CSSTransition
              key={currentImageIndex}
              timeout={3000}
              classNames="caption-fade"
              nodeRef={captionRef}
            >
              <div
                ref={captionRef}
                className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent"
              >
                <p className="text-black-800 text-lg font-medium">
                  {images[currentImageIndex].caption}
                </p>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
      
      <div className="flex-1 p-4 md:p-8">
        <div className="h-full flex">
          {showChat ? (
            <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl border border-gray-800/50 w-full h-full">
              <InterviewChat uid={user.uid} onInterviewStateChange={setShowChat} />
            </div>
          ) : (
            <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl border border-gray-800/50 w-full h-full flex flex-col items-center justify-center p-8 text-center">
              <h2 className="text-2xl font-semibold text-purple-400 mb-4">Ready to Begin Your Interview?</h2>
              <p className="text-gray-400 mb-8 max-w-lg text-left text-justify">
              • For the best experience, we recommend completing the interview in one uninterrupted session. Please find a quiet space and set aside 30 minutes to attempt it with focus. 
              <br />
              • Thereafter, select your preferred interview model. There are two interview models: "Chill" and "Grill". 
              "Chill" model is supportive and conversational. "Grill" model is challenging and combative. 
              <br />
              • After selecting the interview mode, upload your resume and read through the Guidelines that will follow. 
              We will tailor the interview based on your profile. All the best!
              </p>
              <button
                onClick={() => setShowChat(true)}
                className="px-6 py-3 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Begin Interview
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* <HistoryPanel /> */}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [interviewActive] = useState(false);
  const { subscriptionStatus } = useSubscription(user?.uid);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div className="min-h-screen bg-[#0B0F17] text-gray-100 flex flex-col">
        <ScrollToTop />
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 bg-[#0B0F17]/80 backdrop-blur-sm border-b border-gray-800/50 z-50">
          <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-8">
              <Link to="/interview" className="flex items-center gap-2">
                <img src={logo} alt="IIM Interview Logo" className="h-8 w-auto" />
                <span className="text-xl font-semibold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  IIM Interview
                </span>
              </Link>
              <nav className="hidden md:flex items-center gap-6 text-sm">
                <Link 
                  to="/" 
                  className={`text-gray-400 hover:text-white transition-colors ${interviewActive ? 'pointer-events-none opacity-50' : ''}`}
                >
                  About
                </Link>
                <Link 
                  to="/pricing" 
                  className={`text-gray-400 hover:text-white transition-colors ${interviewActive ? 'pointer-events-none opacity-50' : ''}`}
                >
                  Pricing
                </Link>
                <Link 
                  to="/enterprise" 
                  className={`text-gray-400 hover:text-white transition-colors ${interviewActive ? 'pointer-events-none opacity-50' : ''}`}
                >
                  Enterprise
                </Link>
                {user && (
                  <Link 
                    to="/past-interviews" 
                    className={`text-gray-400 hover:text-white transition-colors ${interviewActive ? 'pointer-events-none opacity-50' : ''}`}
                  >
                    Recent Interviews
                  </Link>
                )}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <div className="flex items-center gap-4">
                  <span className="hidden md:inline text-sm text-gray-400">{user.email}</span>
                  {subscriptionStatus && subscriptionStatus !== 'free' && (
                    <span className="hidden md:inline text-sm px-2 py-1 bg-blue-500/10 text-blue-400 rounded">
                      {subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)}
                    </span>
                  )}
                  <button 
                    onClick={async () => {
                      await signOutUser();
                      navigate('/');
                    }} 
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Link to="/signin" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </header>

        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/interview" element={<MainContent />} />
            <Route path="/pricing" element={<Pricing user={user} />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/updates" element={<Updates />} />
            <Route path="/past-interviews" element={<PastInterviews />} />
            <Route path="/feedback/:sessionId" element={<Feedback />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/cancellation-refunds" element={<CancellationRefunds />} />
          </Routes>
        </div>

        {/* Footer Links */}
        <div className="py-6 border-t border-gray-800/50">
          <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-gray-500">
            <Link to="/contact-us" className="hover:text-gray-300 transition-colors">Contact Us</Link>
            <Link to="/updates" className="hover:text-gray-300 transition-colors">Updates</Link>
            <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms and Conditions</Link>
            <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
            <Link to="/shipping-policy" className="hover:text-gray-300 transition-colors">Shipping Policy</Link>
            <Link to="/cancellation-refunds" className="hover:text-gray-300 transition-colors">Cancellation & Refunds</Link>
            <span className="text-gray-600">© 2025 IIM Interview. All rights reserved.</span>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App; 